// PMC: define the floating bar straight away. It used to wait for an idle callback and for
// cart.js + product-form.js, but showing/hiding the bar needs neither; adding to cart is still
// handled by <product-form> once product-form.js has loaded.
(() => {
  themeSettings.disableStickyHeader = () => themeBase.isDesktop;
  themeBase.onDOMContentLoadedTrigger(() => {
    (() => {
      if (!customElements.get('product-footbar')) {
        class ProductFootbar extends HTMLElement {
          constructor() {
            super();

            this.onChangeHandler = this.onChange.bind(this);
            this.checkOverlayHandler = this.checkOverlay.bind(this);
            this.scheduleCheckHandler = this.scheduleCheck.bind(this);
            this.guardNoVariantHandler = this.guardNoVariant.bind(this);
            this.lastVisible = null;
            this.listenersBound = false;
          }

          connectedCallback() {
            this.product = document.querySelector('.product');
            this.productButtonAddToCart = this.product?.querySelector('[name="add"]');
            this.variants = this.querySelector('[name="id"]');

            // Like the main Add to cart button: if the cart scripts haven't loaded yet when the
            // shopper taps, the theme loads them first and then replays the tap.
            const submit = this.querySelector('[name="add"]');
            if (submit && !submit.dataset.requiredScripts) {
              submit.dataset.requiredScripts = '["cart.js", "product-form.js", "product-info.js"]';
            }

            if (!this.variants || this.listenersBound) return;

            this.variants.addEventListener('change', this.onChangeHandler);
            // The option dropdown is hidden on mobile. If no option has been chosen yet
            // (placeholder selected), send the shopper to the picker instead of failing.
            this.addEventListener('click', this.guardNoVariantHandler, true);
            window.addEventListener('MutationObserverLoaded', this.checkOverlayHandler);
            window.addEventListener('throttle30fpsMove', this.checkOverlayHandler);
            window.addEventListener('scroll', this.scheduleCheckHandler, { passive: true });
            window.addEventListener('resize', this.scheduleCheckHandler, { passive: true });
            window.addEventListener('orientationchange', this.scheduleCheckHandler);
            this.listenersBound = true;
            this.checkOverlay();
          }

          disconnectedCallback() {
            if (this.listenersBound) {
              this.variants?.removeEventListener('change', this.onChangeHandler);
              this.removeEventListener('click', this.guardNoVariantHandler, true);
              window.removeEventListener('MutationObserverLoaded', this.checkOverlayHandler);
              window.removeEventListener('throttle30fpsMove', this.checkOverlayHandler);
              window.removeEventListener('scroll', this.scheduleCheckHandler);
              window.removeEventListener('resize', this.scheduleCheckHandler);
              window.removeEventListener('orientationchange', this.scheduleCheckHandler);
              this.listenersBound = false;
            }

            this.lastVisible = null;
            document.body.classList.remove('product-footbar-visible');
            document.documentElement.style.removeProperty('--product-footbar-height');
          }

          onChange(event) {
            const options = event.target?.querySelectorAll('option');
            const productVariantSwitcher = document.querySelector(`variant-radios[data-section="${this.dataset.sectionId}"]`) || document.querySelector(`variant-selects[data-section="${this.dataset.sectionId}"]`);
            const variantDataElement = this.product?.querySelector('[type="application/json"]');

            if (!options || !productVariantSwitcher || !variantDataElement) return;

            let variantData;
            try {
              variantData = JSON.parse(variantDataElement.textContent);
            } catch (error) {
              console.error('Unable to read sticky add-to-cart variant data.', error);
              return;
            }

            options.forEach(element => {
              if (element.selected) {
                const currentVariant = variantData?.variants?.find((variant) => {
                  return variant.id == element.value;
                });
                if (!currentVariant) return;

                if (typeof productVariantSwitcher.changeOptions === 'function') {
                  productVariantSwitcher.changeOptions(currentVariant.options);
                }
                if (typeof productVariantSwitcher.onVariantChange === 'function') {
                  productVariantSwitcher.onVariantChange({
                    isFootbar: true
                  });
                }
              }
            });
          }

          checkOverlay() {
            if (!this.isConnected) return;

            // Re-resolve the main button if the product section was re-rendered.
            if (!this.productButtonAddToCart || !this.productButtonAddToCart.isConnected) {
              this.product = document.querySelector('.product');
              this.productButtonAddToCart = this.product?.querySelector('[name="add"]');
            }

            const isMobile = !window.matchMedia('(min-width: 1025px)').matches;
            let isVisible = false;

            if (isMobile) {
              // Show the floating bar whenever the main add-to-cart button is not on screen:
              // below the fold, scrolled past, hidden, or missing.
              const button = this.productButtonAddToCart;
              const rect = button ? button.getBoundingClientRect() : null;
              const isRendered = !!rect && (rect.width > 0 || rect.height > 0) && button.getClientRects().length > 0;
              const footbarHeight = this.offsetHeight || 0;
              const viewportBottom = window.innerHeight - footbarHeight;
              // A floating header covers the top of the screen, so a button tucked behind it is not visible.
              const stickyHeader = document.querySelector('.sticky-header__content--sticky');
              const headerRect = stickyHeader ? stickyHeader.getBoundingClientRect() : null;
              const viewportTop = headerRect && getComputedStyle(stickyHeader).position === 'fixed' ? Math.max(0, headerRect.bottom) : 0;
              const middle = rect ? (rect.top + rect.bottom) / 2 : 0;
              const mainButtonOnScreen = isRendered && middle > viewportTop && middle < viewportBottom;
              isVisible = !mainButtonOnScreen;
            }

            if (this.lastVisible === isVisible) return;
            this.lastVisible = isVisible;

            this.classList[isVisible ? 'add' : 'remove']('product-footbar__wrapper--visible');
            document.body.classList[isVisible ? 'add' : 'remove']('product-footbar-visible');

            if (isVisible) {
              const height = this.getBoundingClientRect().height;
              if (height) {
                document.documentElement.style.setProperty('--product-footbar-height', `${(height / 10).toFixed(1)}rem`);
              }
            } else {
              document.documentElement.style.setProperty('--product-footbar-height', '');
            }
          }

          guardNoVariant(event) {
            if (!event.target.closest('[name="add"]') || !this.variants) return;
            const option = this.variants.options ? this.variants.options[this.variants.selectedIndex] : null;
            if (this.variants.value && !(option && option.disabled)) return;

            event.preventDefault();
            event.stopPropagation();
            const picker = document.querySelector(`variant-radios[data-section="${this.dataset.sectionId}"], variant-selects[data-section="${this.dataset.sectionId}"]`);
            if (picker) {
              const top = picker.getBoundingClientRect().top + window.scrollY - 120;
              window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
            }
          }

          scheduleCheck() {
            if (this.checkQueued) return;
            this.checkQueued = true;
            window.requestAnimationFrame(() => {
              this.checkQueued = false;
              this.checkOverlay();
            });
          }
        }

        customElements.define('product-footbar', ProductFootbar);
      }
    })();
  });
})();
